// 1 person (AdminPerson.dc.html): who they are, what they can open and why, the free things with holds,
// progress, logging in, notes and history, with every access action BUILD-PLAN stage 5 asks for and the
// "This person" box. For each mastermind, owner and admin get a card after Access that opens the member
// view in Masterminds and the accountability log (docs/mastermind-admin-plan.md 3.4); the old printable
// report's address, ?evidence=<product id>, sends on to the log. Owner and admin act; support sees progress, logins and history and can resend the
// login email; a host sees only their product's rows (the reads scope them). Amounts are read live from
// Stripe for owner and admin (stripe-prices) and stored nowhere. Products not moved into the hub show
// only as labelled ownership outside it. Owner and admin also see the Referrals card (decision 55), before History.
// On a phone, Referrals, History and This person fold to their headings (people/Cards.jsx PhoneFold), and each access
// row is 1 line that opens on tap (the critique's admin 17).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { navigate, useLocation } from '../../lib/router.jsx'
import { setTitle } from '../../config/site.js'
import { initials } from '../../lib/format.js'
import { Button, ButtonLink, Card, Crumbs, Loading, PageHeader, Problem, ViewAsButton, useAdmin, adminPath, ROLE_NAMES } from '../ui/index.js'
import { PERSON, CHANGE, THIS, firstOf, boardDay, overdueAlert, actorsByAccess } from '../people/words.js'
import { loadPerson, amountIds, loadAmounts } from '../people/data.js'
import { AccessCard, canSendCard, giveSevenDays, sendCardLink } from '../people/AccessCard.jsx'
import { FreeThings, RegisterCard, ProgressCard, LoginCard, NotesCard, HistoryCard, PhoneFold, historyCount } from '../people/Cards.jsx'
import { ThisPerson } from '../people/ThisPerson.jsx'
import { GrantDialog } from '../people/GrantDialog.jsx'
import { OpenSpaceDialog } from '../people/OpenSpaceDialog.jsx'
import { hasMastermind, loadStanding, loadGroupSessions } from '../mastermind/data.js'
import { MastermindCard } from '../mastermind/PersonCard.jsx'
import { PersonReferrals } from '../referrals/PersonCard.jsx'
import '../../styles/admin-people.css'

export default function Person({ me, role, route }) {
  const { api, setFlash } = useAdmin()
  const id = route.params.id
  const oa = role === 'owner' || role === 'admin'
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const [amounts, setAmounts] = useState({})
  const [extended, setExtended] = useState({})
  const [cardSent, setCardSent] = useState({})
  const [busy, setBusy] = useState(null)
  const [alertError, setAlertError] = useState(null)
  const [grant, setGrant] = useState(null)
  const [opening, setOpening] = useState(false)
  const apiRef = useRef(api)
  apiRef.current = api
  const seq = useRef(0)

  const load = useCallback(async ({ quiet = false } = {}) => {
    // the screen can stay drawn a moment while the address moves on to another (the old report's address sends
    // on to Masterminds): with no person in it there's nothing to read
    if (!id) return
    const n = ++seq.current
    if (!quiet) setState(s => ({ ...s, status: s.data ? s.status : 'loading', error: null }))
    try {
      const loaded = await loadPerson(apiRef.current, id, role)
      // the Accelerator's standing (hub.mastermind_standing): the access row's month, the progress line
      // and the Accelerator panel, for someone with a mastermind in the hub
      const standing = loaded && hasMastermind(loaded) ? await loadStanding(apiRef.current, id) : []
      // and its group sessions, so History says which session of the group a register line is
      const groupSessions = standing.length ? await loadGroupSessions(apiRef.current, standing.map(st => st.product_id)) : []
      const data = loaded ? { ...loaded, standing, groupSessions } : loaded
      if (n !== seq.current) return
      setState({ status: 'ready', data, error: null })
      if (data && oa) {
        const body = amountIds(data.billing, data.history)
        loadAmounts(apiRef.current, body).then(a => { if (n === seq.current) setAmounts(a || {}) })
      }
    } catch (e) {
      if (n === seq.current) setState(s => ({ ...s, status: 'error', error: e }))
    }
  }, [id, role, oa])
  useEffect(() => { setExtended({}); setCardSent({}); load() }, [load])

  const data = state.data
  const person = data?.person
  // the old printable report's address, /admin/people/<id>?evidence=<product id>, sends on to the log
  const evidenceFor = useLocation().query.get('evidence')
  const evidenceSlug = evidenceFor ? data?.catalogue?.find(p => p.id === evidenceFor && p.type === 'mastermind')?.slug : null
  useEffect(() => { if (evidenceSlug) navigate(adminPath.mmLog(evidenceSlug, id), { replace: true }) }, [evidenceSlug, id])
  useEffect(() => { if (!evidenceFor) setTitle(person ? `Admin: ${person.full_name}` : 'Admin: Person') }, [person, evidenceFor])

  const ctx = useMemo(() => {
    if (!data) return null
    const { granters, revokers } = actorsByAccess(data.history)
    return {
      productsById: new Map(data.catalogue.map(p => [p.id, p])),
      accessById: new Map(data.access.map(a => [a.id, a])),
      billingByAccess: new Map(data.billing.map(b => [b.access_id, b])),
      cohortsById: new Map(data.cohorts.map(c => [c.id, c])),
      sessionsById: new Map((data.groupSessions || []).map(s => [s.id, s])),
      amounts, granters, revokers, first: firstOf(data.person),
    }
  }, [data, amounts])

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error' && !data) return <Problem error={state.error} onRetry={() => load()} />
  if (!person) {
    return (
      <>
        <Crumbs items={[{ label: PERSON.crumb, to: adminPath.people() }, { label: PERSON.notFoundTitle }]} />
        <PageHeader eyebrow={PERSON.crumb} title={PERSON.notFoundTitle} />
        <Card title={PERSON.notFound}>
          <p className="ad-lede">{PERSON.notFoundLine}</p>
          <p><ButtonLink to={adminPath.people()} size="sm">{PERSON.back}</ButtonLink></p>
        </Card>
      </>
    )
  }

  const first = firstOf(person)
  const changed = message => { if (message) setFlash({ text: message }); load({ quiet: true }) }

  if (person.merged_into_id || person.anonymised_at) {
    return (
      <>
        <Crumbs items={[{ label: PERSON.crumb, to: adminPath.people() }, { label: person.anonymised_at ? PERSON.deleted : person.full_name }]} />
        <PageHeader eyebrow={PERSON.crumb} title={person.anonymised_at ? PERSON.deleted : person.full_name} />
        <Card title={person.anonymised_at ? PERSON.deleted : PERSON.merged}>
          <p className="ad-lede">{person.anonymised_at ? PERSON.deletedLine(boardDay(person.anonymised_at)) : PERSON.mergedLine('another person')}</p>
          {person.merged_into_id && <p><ButtonLink to={adminPath.person(person.merged_into_id)} size="sm">{PERSON.mergedOpen('the kept person')}</ButtonLink></p>}
        </Card>
      </>
    )
  }

  if (evidenceSlug) return <Loading />

  const productsById = ctx.productsById
  const billingBy = ctx.billingByAccess
  const overdue = data.access.filter(a => a.status === 'overdue' && productsById.get(a.product_id)?.moved_in)
  const joined = [...data.access.map(a => a.starts_at), ...data.spaces.map(s => s.opened_at)].filter(Boolean).sort()[0] || person.created_at
  const line = [person.email, person.location, PERSON.memberSince(boardDay(joined))].filter(Boolean)
    .map(s => s.replace(/\.$/, '')).join('. ') + '.'
  const data2 = { ...data, granters: ctx.granters }

  async function grace(row) {
    if (busy) return
    setBusy(`grace:${row.id}`); setAlertError(null)
    try {
      const d = boardDay(await giveSevenDays(api, row))
      setExtended(s => ({ ...s, [row.id]: d }))
      changed(CHANGE.done.grace(d))
    } catch (e) { setAlertError(e) } finally { setBusy(null) }
  }
  async function card(row) {
    if (busy) return
    setBusy(`card:${row.id}`); setAlertError(null)
    try {
      const answer = await sendCardLink(api, { personId: person.id, row })
      setCardSent(s => ({ ...s, [row.id]: true }))
      setFlash({ text: answer.message })
    } catch (e) { setAlertError(e) } finally { setBusy(null) }
  }
  async function endSpace(space) {
    try {
      const answer = await api.client_space({ action: 'end', space_id: space.id })
      changed(answer.message)
      return true
    } catch (e) {
      setFlash({ text: e.message, tone: 'problem' })
      return false
    }
  }

  return (
    <>
      <Crumbs items={[{ label: PERSON.crumb, to: adminPath.people() }, { label: person.full_name }]} />
      <section className="apl-person-head">
        <div className="apl-who">
          <span className="apl-avatar" aria-hidden="true">{initials(person.full_name)}</span>
          <div className="apl-who-text">
            <h1 className="ad-h1 apl-h1">{person.full_name}</h1>
            <span className="apl-who-line">{line}</span>
            {(person.is_test || person.role) && (
              <span className="apl-who-tags">
                {person.role && <span className="ad-tag">{PERSON.staffTag(ROLE_NAMES[person.role] || person.role)}</span>}
                {person.is_test && <span className="ad-tag">{PERSON.testLogin}</span>}
              </span>
            )}
          </div>
        </div>
        {oa && (
          <div className="apl-head-actions">
            <ViewAsButton person={person} />
            <Button kind="ink" onClick={() => setGrant({ product: null })}>{PERSON.grant}</Button>
          </div>
        )}
      </section>

      {oa && overdue.map(row => {
        const b = billingBy.get(row.id)
        const canCard = canSendCard(b, data.hasCustomer)
        return (
          <section key={row.id} className="apl-alert" aria-label={productsById.get(row.product_id)?.name}>
            <span className="apl-alert-text">{overdueAlert(row, productsById.get(row.product_id), first, { extendedTo: extended[row.id] })}</span>
            <span className="apl-alert-actions">
              {canCard && <Button kind="ink" size="sm" busy={busy === `card:${row.id}`} disabled={cardSent[row.id]} onClick={() => card(row)}>{cardSent[row.id] ? PERSON.cardSent : PERSON.cardLink}</Button>}
              {row.suspend_on && <Button size="sm" busy={busy === `grace:${row.id}`} disabled={Boolean(extended[row.id])} onClick={() => grace(row)}>{extended[row.id] ? PERSON.extended(extended[row.id]) : PERSON.extend}</Button>}
            </span>
          </section>
        )
      })}
      {alertError && <Problem error={alertError} />}

      <AccessCard data={data2} amounts={amounts} role={role} first={first} extended={extended}
        onGrace={grace} onCard={card} onChanged={changed}
        onGrantAgain={productId => setGrant({ product: productId })}
        onEndSpace={endSpace} onOpenSpace={openIds => setOpening({ openProductIds: openIds || [] })} />

      {oa && (data.standing || []).filter(st => productsById.get(st.product_id)?.moved_in && productsById.get(st.product_id)?.type === 'mastermind').map(st => (
        <MastermindCard key={st.product_id} personId={person.id} product={productsById.get(st.product_id)} />
      ))}

      {role !== 'host' && <FreeThings data={data} role={role} first={first} onChanged={() => load({ quiet: true })} />}
      {/* a replay's register pages: the free account 1 made, and what they registered for */}
      {role !== 'host' && <RegisterCard data={data} />}

      <div className="apl-split">
        <ProgressCard data={data} role={role} first={first} onIssued={changed} />
        <div className="apl-col">
          <LoginCard data={data} role={role} me={me} />
          {oa && <NotesCard data={data} onChanged={() => load({ quiet: true })} />}
        </div>
      </div>

      {/* referrals (decision 55): their code, what they can refer and their introductions, owner and admin only */}
      {oa && <PersonReferrals person={person} onChanged={() => load({ quiet: true })} />}

      {/* the fold sits outside History, which is drawn afresh when its first page changes, so it stays open */}
      <PhoneFold title={PERSON.history} count={historyCount(data, ctx)}>
        <HistoryCard key={`${person.id}-${data.history.length}-${data.history[0]?.happened_at || ''}`} data={data} ctx={ctx} />
      </PhoneFold>

      {oa && (
        <PhoneFold title={THIS.title}>
          <ThisPerson data={data} role={role} me={me} onChanged={changed}
            onMerged={answer => {
              setFlash({ text: answer.message, keep: true })
              if (answer.kept_person_id && answer.kept_person_id !== person.id) navigate(adminPath.person(answer.kept_person_id))
              else load({ quiet: true })
            }}
            onDeleted={answer => { setFlash({ text: answer.message, keep: true }); navigate(adminPath.people()) }} />
        </PhoneFold>
      )}

      {grant && (
        <GrantDialog person={person} access={data.access.filter(a => productsById.get(a.product_id)?.moved_in)} catalogue={data.catalogue}
          cohorts={data.cohorts} initialProduct={grant.product} onClose={() => setGrant(null)}
          onGranted={answer => { setGrant(null); changed(answer.message) }} />
      )}
      {opening && (
        <OpenSpaceDialog person={person} openProductIds={opening.openProductIds || []} onClose={() => setOpening(false)}
          onOpened={answer => { setOpening(false); changed(answer.message) }} />
      )}
    </>
  )
}
